import React, { useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useForm, useFieldArray, Controller } from 'react-hook-form';
import type { Resolver } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';


import { Box } from 'ui/box';
import { Stack } from 'ui/stack';

import Button from 'components/partials/button/button';
import IconButton from 'components/partials/button/icon-button';
import { Checkbox } from 'ui/checkbox';
import Input from 'components/partials/input/input';
import Modal from 'components/partials/modal/modal';

import {
  isNonAidRange,
  isNonAgiRange,
  isNonAgiAvailable,
  getRangesLabel,
} from '../definition-tab.constants';

import Icons from 'assets/icons';

import { rangesSchema } from './edit-ranges-modal.schema';

const FORM_ID = 'edit-ranges-form';

interface EditableRange {
  from: number | null;
  to: number | null;
  nonAgi?: boolean;
}

interface EditRangesFormValues {
  includeNonAid: boolean;
  includeNonAgi: boolean;
  ranges: EditableRange[];
}

interface EditRangesModalProps {
  awardType: string;
  ranges: EditableRange[];
  onSave: (ranges: EditableRange[]) => void;
  onClose: () => void;
}

const EditRangesModal = ({
  awardType,
  ranges,
  onSave,
  onClose,
}: EditRangesModalProps): React.JSX.Element | null => {
  const defaultValues = useMemo(
    () => ({
      includeNonAid: ranges.some(isNonAidRange),
      includeNonAgi: ranges.some(isNonAgiRange),
      ranges: ranges
        .filter((r) => !isNonAidRange(r) && !isNonAgiRange(r))
        .map((r) => ({ from: r.from ?? null, to: r.to ?? null })),
    }),
    [ranges]
  );

  const {
    control,
    handleSubmit,
    watch,
    trigger,
    formState: { isValid, errors },
  } = useForm<EditRangesFormValues>({
    defaultValues,
    resolver: yupResolver(
      rangesSchema
    ) as unknown as Resolver<EditRangesFormValues>,
    mode: 'onChange',
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'ranges',
  });

  const watchedRanges = watch('ranges');

  useEffect(() => {
    trigger();
  }, [trigger]);

  const handleAddRange = (): void => {
    const lastRange = watchedRanges[watchedRanges.length - 1];
    const nextFrom = lastRange?.to != null ? lastRange.to + 1 : null;

    append({ from: nextFrom, to: null });
    trigger();
  };

  const handleRemoveRange = (index: number): void => {
    remove(index);
    trigger();
  };

  const lastIndex = watchedRanges.length - 1;
  const lastRange = watchedRanges[lastIndex];
  const lastRangeErrors = errors?.ranges?.[lastIndex];

  const isLastRangeComplete =
    !fields.length ||
    (lastRange?.to != null && !lastRangeErrors?.from && !lastRangeErrors?.to);

  const hasIncompleteRow = watchedRanges.some(
    (r, index) => index < watchedRanges.length - 1 && r?.to == null
  );

  const onFormSubmit = (data: EditRangesFormValues): void => {
    const result: EditableRange[] = [
      ...(data.includeNonAid ? [{ from: null, to: null }] : []),
      ...(isNonAgiAvailable(awardType) && data.includeNonAgi
        ? [{ from: null, to: null, nonAgi: true }]
        : []),
      ...data.ranges,
    ];

    onSave(result);
  };

  const footer = (
    <Stack direction="row" sx={{ alignItems: 'center', gap: 2 }}>
      <Button
        type="button"
        variant="brand-filled"
        size="md"
        disabled={!isValid || hasIncompleteRow}
        onClick={handleSubmit(onFormSubmit)}
        data-testid="save-ranges-button"
      >
        Save
      </Button>
      <Button
        type="button"
        variant="reserve-text"
        size="md"
        onClick={onClose}
        data-testid="cancel-ranges-button"
      >
        Cancel
      </Button>
    </Stack>
  );

  const container =
    typeof document !== 'undefined'
      ? document.getElementById('modal-container')
      : null;

  if (!container) return null;

  return createPortal(
    <Modal
      headerText={getRangesLabel(awardType)}
      onCloseCallback={onClose}
      showCloseButton
      disableBackdropClick
      footer={footer}
      skipUnmountOnClose
      primaryAction={{
        onClick: handleSubmit(onFormSubmit),
        disabled: !isValid || hasIncompleteRow,
      }}
      data-testid="edit-ranges-modal"
    >
      <form
        id={FORM_ID}
        className="mt-4 flex flex-col gap-3"
        onSubmit={handleSubmit(onFormSubmit)}
      >
        <Controller
          name="includeNonAid"
          control={control}
          render={({ field }) => (
            <Checkbox
              id="include-non-aid"
              label="Include Non-Aid column"
              value="non-aid"
              checked={field.value}
              onChange={() => field.onChange(!field.value)}
              size="small"
              data-testid="checkbox-include-non-aid"
            />
          )}
        />
        {isNonAgiAvailable(awardType) && (
          <Controller
            name="includeNonAgi"
            control={control}
            render={({ field }) => (
              <Checkbox
                id="include-non-agi"
                label="Include Non-AGI column"
                value="non-agi"
                checked={field.value}
                onChange={() => field.onChange(!field.value)}
                size="small"
                data-testid="checkbox-include-non-agi"
              />
            )}
          />
        )}
        <div className="max-h-[400px] overflow-y-auto overflow-x-hidden flex flex-col gap-3 scrollbar-thin">
          {fields.map((field, index) => (
            <div
              key={field.id}
              className={
                'grid grid-cols-[minmax(0,1fr)_minmax(0,1fr)_2.5rem] ' +
                'gap-2 items-start pr-2'
              }
              data-testid={`range-row-${index}`}
            >
              <Box sx={{ minWidth: 0 }}>
                <Controller
                  name={`ranges.${index}.from`}
                  control={control}
                  render={({ field: f }) => (
                    <Input
                      label="From"
                      labelFor={`range-from-${index}`}
                      labelInTop
                      type="number"
                      placeholder=""
                      startAdornment="$"
                      value={f.value ?? ''}
                      onChange={(e) => {
                        const v = e.target.value;

                        f.onChange(v === '' ? null : Number(v));
                        trigger();
                      }}
                      ref={f.ref}
                      errorsNames={errors?.ranges?.[index]?.from || {}}
                      data-testid={`range-from-${index}`}
                    />
                  )}
                />
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <Controller
                  name={`ranges.${index}.to`}
                  control={control}
                  render={({ field: f }) => (
                    <Input
                      label="To"
                      labelFor={`range-to-${index}`}
                      labelInTop
                      type="number"
                      placeholder=""
                      startAdornment="$"
                      value={f.value ?? ''}
                      onChange={(e) => {
                        const v = e.target.value;

                        f.onChange(v === '' ? null : Number(v));
                        trigger();
                      }}
                      ref={f.ref}
                      errorsNames={errors?.ranges?.[index]?.to || {}}
                      data-testid={`range-to-${index}`}
                    />
                  )}
                />
              </Box>
              <Stack direction="row" sx={{ pt: 5.5, justifyContent: 'center' }}>
                <IconButton
                  variant="reserve-text"
                  size="sm"
                  icon={<Icons.Delete />}
                  onClick={() => handleRemoveRange(index)}
                  aria-label="Remove range"
                  className="shrink-0"
                  data-testid={`remove-range-${index}`}
                />
              </Stack>
            </div>
          ))}
        </div>
        <Button
          type="button"
          variant="brand-text-link"
          size="sm"
          iconBefore={<Icons.Plus />}
          onClick={handleAddRange}
          disabled={!isLastRangeComplete}
          className="self-start"
          data-testid="add-range-button"
        >
          Add range
        </Button>
      </form>
    </Modal>,
    container
  );
};

export default EditRangesModal;
